/**
 * Every tunable number in the product lives here.
 *
 * The range thresholds in particular will be re-tuned from field data (`ProductOverview.md` §6),
 * so they must never be inlined at a call site.
 */

import { MAX_PARTICIPANTS } from './participants';

/** Range-layered behaviour — `ProductOverview.md` §4.5. */
export const RANGE = {
  /** At or below this separation, bearing stops being trustworthy: arrow → hot/cold ring. */
  hotColdMetres: 30,
  /** At or below this, the product hands off to human eyes and offers the beacon. */
  handoffMetres: 15,
  /** Staying inside `handoffMetres` for this long marks the session converged. */
  convergedHoldMs: 10_000,
  /** Hysteresis so a mode does not flicker on the threshold. */
  hysteresisMetres: 3,
} as const;

/** Honesty indicators — `ProductOverview.md` §4.6. */
export const CONFIDENCE = {
  /** Reported GPS accuracy (metres) at or below which a fix is "good". */
  goodMetres: 15,
  /** ...and at or below which it is "okay". Worse than this is "poor". */
  okayMetres: 40,
  /** Fixes worse than this are excluded from the arrow computation entirely. */
  rejectMetres: 100,
} as const;

/** Peer signal staleness — `ProductOverview.md` §4.6. */
export const STALENESS = {
  /** Past this age the arrow desaturates and is labelled stale. */
  staleMs: 8_000,
  /** Past this age we escalate to "waiting for <name>'s signal". */
  lostMs: 30_000,
} as const;

/** Arrow rendering and smoothing. */
export const ARROW = {
  /** Fraction of angular error remaining after one second. Lower = snappier. */
  headingSmoothing: 0.02,
  /** The bearing needle is smoothed more gently than the compass rose. */
  bearingSmoothing: 0.05,
  /** Within this many degrees of the bearing, the arrow lights up: "on course". */
  onCourseDegrees: 12,
  /** Hysteresis so the on-course glow does not strobe at the boundary. */
  onCourseReleaseDegrees: 20,
  /** Cap the device pixel ratio — battery, per `ProductOverview.md` §6. */
  maxPixelRatio: 2,
} as const;

/** Sessions — `ProductOverview.md` §4.1. */
export const SESSION = {
  /** Default session lifetime, when the creator does not choose one. */
  ttlSeconds: 60 * 60,
  /** How much one extension buys. */
  extensionSeconds: 60 * 60,
  /**
   * How many times a session may be extended.
   *
   * Was once, when every session was an hour. A weekend festival session is chosen up front
   * rather than extended sixteen times, so the allowance exists for the pickup that overran,
   * not as a way to keep a room alive indefinitely.
   */
  maxExtensions: 3,
  /** Hard ceiling on room size. A session may choose less; it may never choose more. */
  maxParticipants: MAX_PARTICIPANTS,
  /** The hero flow is still two people, so that is what one tap gets you. */
  defaultCapacity: 2,
  /** Length of the share code. Crypto-random, unguessable. */
  codeLength: 12,
  /** Optional first name, used only to render "Maria is 220 m away". */
  maxNameLength: 24,
} as const;

/**
 * The lifetimes a creator can pick from.
 *
 * Sixty minutes is right for a pickup and wrong for a three-day festival, and a room that dies
 * on the Saturday morning of a weekend someone planned around it is worse than no room at all.
 * Every option still ends by itself — nothing here is permanent.
 */
export const SESSION_LENGTHS = [
  { id: 'hour', label: '1 hour', seconds: 60 * 60 },
  { id: 'day', label: '8 hours', seconds: 8 * 60 * 60 },
  { id: 'weekend', label: 'All weekend', seconds: 72 * 60 * 60 },
] as const;

export type SessionLengthId = (typeof SESSION_LENGTHS)[number]['id'];

/** Position reporting. */
export const POSITION = {
  /** Client-side throttle: at most one position message per second. */
  minSendIntervalMs: 1000,
  /** Drop a fix entirely if the browser reports it older than this. */
  maxFixAgeMs: 60_000,
} as const;
